import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

const functions = getFunctions();
const getCommissionSettings       = httpsCallable(functions, 'getCommissionSettings');
const setCommissionSettings       = httpsCallable(functions, 'setCommissionSettings');
const getEmployeeCommissionRates  = httpsCallable(functions, 'getEmployeeCommissionRates');
const setEmployeeCommissionRate   = httpsCallable(functions, 'setEmployeeCommissionRate');

const todayISO = new Date().toISOString().slice(0, 10);

const APPLIES_TO_OPTIONS = [
  { value: 'reservation_total', label: 'Reservation Total' },
  { value: 'food_only',         label: 'Food Sales Only'   },
  { value: 'beverage_only',     label: 'Beverage Sales Only' },
];
const APPLIES_TO_SHORT = {
  reservation_total: 'Total',
  food_only:         'Food',
  beverage_only:     'Drinks',
};

const HYPO_DEFAULTS = { food: '800', drink: '400' };

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const MODE_COLOR = {
  none:       'bg-neutral-100 text-neutral-500 border-neutral-200',
  individual: 'bg-blue-50 text-blue-700 border-blue-200',
  pooled:     'bg-violet-50 text-violet-700 border-violet-200',
};

const revenueFor = (appliesTo, hypo) => {
  const food  = parseFloat(hypo.food)  || 0;
  const drink = parseFloat(hypo.drink) || 0;
  if (appliesTo === 'food_only')     return food;
  if (appliesTo === 'beverage_only') return drink;
  return food + drink;
};

// ─── Inline field helper ──────────────────────────────────────────────────────

const Field = ({ label, value, onChange, type = 'text', placeholder = '' }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-500 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
    />
  </div>
);

// ─── CommissionSettings ───────────────────────────────────────────────────────

const CommissionSettings = ({ restaurantId, onDirtyChange }) => {

  // ── Pool settings state ───────────────────────────────────────────────────
  const [poolEnabled, setPoolEnabled]       = useState(false);
  const [poolInflowRate, setPoolInflowRate] = useState('5');
  const [poolAppliesTo, setPoolAppliesTo]   = useState('reservation_total');
  const [positions, setPositions]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [saved, setSaved]                   = useState(false);
  const [error, setError]                   = useState('');
  const [dirty, setDirty]                   = useState(false);

  // ── Hypothetical state ────────────────────────────────────────────────────
  const [hypo, setHypo]                         = useState(HYPO_DEFAULTS);
  const [hypoHours, setHypoHours]               = useState({});
  const [hypoReservations, setHypoReservations] = useState('5');

  // ── Individual commission state ───────────────────────────────────────────
  const [indivEmps, setIndivEmps]       = useState([]);
  const [indivRates, setIndivRates]     = useState({});  // { empId: currentRateDoc }
  const [indivLoading, setIndivLoading] = useState(true);

  // ── Inline edit form state ────────────────────────────────────────────────
  const [editingEmpId, setEditingEmpId] = useState(null);
  const [editForm, setEditForm]         = useState({
    mode: 'individual', type: 'percent', rate: '',
    appliesTo: 'reservation_total', positionId: '', tierId: '',
    pointsOverride: '', effectiveStart: todayISO, reason: '',
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');

  const saveFnRef = useRef(null);

  const setField = (key) => (val) => setEditForm(f => ({ ...f, [key]: val }));

  const markDirty = () => {
    if (!dirty) {
      setDirty(true);
      onDirtyChange?.(true, () => saveFnRef.current?.());
    }
  };

  // ── Load pool settings ────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCommissionSettings({ restaurantId });
      const s = res.data.settings;
      setPoolEnabled(!!s.poolEnabled);
      setPoolInflowRate(String(s.poolInflowRate ?? 5));
      setPoolAppliesTo(s.poolInflowAppliesTo || 'reservation_total');
      setPositions(
        (s.positions || []).map(pos => ({
          ...pos,
          tiers: (pos.tiers || []).map(t => ({ ...t })),
        }))
      );
      setDirty(false);
      onDirtyChange?.(false, null);
    } catch (err) {
      setError(err.message || 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // ── Load individual commission employees + their current rates ─────────────
  // Uses onSnapshot so any commissionMode change on an employee doc (including
  // after a save from this component) automatically re-fetches all rates.

  const loadRatesForEmps = useCallback(async (emps) => {
    if (emps.length === 0) { setIndivRates({}); return; }
    const rateMap = {};
    await Promise.all(
      emps.map(async (emp) => {
        try {
          const res = await getEmployeeCommissionRates({ restaurantId, employeeId: emp.id });
          const current = (res.data.rates || [])[0];
          if (current) rateMap[emp.id] = current;
        } catch (_) { /* non-fatal */ }
      })
    );
    setIndivRates(rateMap);
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!restaurantId) return;
    setIndivLoading(true);

    const empRef = collection(db, 'restaurants_data', restaurantId, 'employees');
    const q = query(
      empRef,
      where('commissionMode', '==', 'individual'),
      where('isActive', '==', true)
    );

    const unsub = onSnapshot(q, async (snap) => {
      const emps = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setIndivEmps(emps);
      await loadRatesForEmps(emps);
      setIndivLoading(false);
    }, () => { setIndivLoading(false); });

    return () => unsub();
  }, [restaurantId, loadRatesForEmps]);

  // ── Save pool settings ────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    for (const pos of positions) {
      if (!pos.name.trim()) {
        setSaving(false); setError('All positions need a name.'); throw new Error('validation');
      }
      for (const tier of pos.tiers) {
        if (!tier.name.trim()) {
          setSaving(false); setError(`All tiers in "${pos.name}" need a name.`); throw new Error('validation');
        }
        if (isNaN(parseFloat(tier.points)) || parseFloat(tier.points) <= 0) {
          setSaving(false); setError(`Tier "${tier.name}" needs a weight greater than 0.`); throw new Error('validation');
        }
      }
    }
    try {
      await setCommissionSettings({
        restaurantId,
        poolEnabled,
        poolInflowRate: parseFloat(poolInflowRate) || 0,
        poolInflowAppliesTo: poolAppliesTo,
        poolDistribution: 'weighted_hours',
        positions: positions.map(pos => ({
          id: pos.id,
          name: pos.name.trim(),
          tiers: pos.tiers.map(t => ({
            id: t.id,
            name: t.name.trim(),
            points: parseFloat(t.points) || 1,
          })),
        })),
      });
      setSaved(true);
      setDirty(false);
      onDirtyChange?.(false, null);
      // Refresh individual rates so pooled position labels stay current
      loadRatesForEmps(indivEmps);
    } catch (err) {
      if (err.message !== 'validation') setError(err.message || 'Failed to save.');
      throw err;
    } finally {
      setSaving(false);
    }
  };
  saveFnRef.current = handleSave;

  // ── Position mutations ────────────────────────────────────────────────────

  const addPosition = () => {
    setPositions(prev => [
      ...prev,
      { id: uid(), name: '', tiers: [{ id: uid(), name: 'Tier 1', points: 1 }] },
    ]);
    markDirty();
  };

  const removePosition  = (posId) => { setPositions(prev => prev.filter(p => p.id !== posId)); markDirty(); };

  const updatePosition  = (posId, field, value) => {
    setPositions(prev => prev.map(p => p.id === posId ? { ...p, [field]: value } : p));
    markDirty();
  };

  const addTier = (posId) => {
    setPositions(prev => prev.map(p => {
      if (p.id !== posId) return p;
      const nextNum = p.tiers.length + 1;
      return { ...p, tiers: [...p.tiers, { id: uid(), name: `Tier ${nextNum}`, points: nextNum }] };
    }));
    markDirty();
  };

  const removeTier = (posId, tierId) => {
    setPositions(prev => prev.map(p => p.id !== posId ? p : { ...p, tiers: p.tiers.filter(t => t.id !== tierId) }));
    markDirty();
  };

  const updateTier = (posId, tierId, field, value) => {
    setPositions(prev => prev.map(p => {
      if (p.id !== posId) return p;
      return { ...p, tiers: p.tiers.map(t => t.id === tierId ? { ...t, [field]: value } : t) };
    }));
    markDirty();
  };

  // ── Hypothetical calculations ─────────────────────────────────────────────

  const hypoTotal = (parseFloat(hypo.food) || 0) + (parseFloat(hypo.drink) || 0);

  // Single global pool — industry standard: one inflow rate × one revenue base = one pool total
  const hypoBase = revenueFor(poolAppliesTo, hypo);
  const hypoPool = hypoBase * (parseFloat(poolInflowRate) || 0) / 100;

  // Default to 6 hrs when the field hasn't been touched
  const getHypoHours = (tierId) =>
    parseFloat(hypoHours[tierId] !== undefined ? hypoHours[tierId] : '6') || 0;

  // Global weighted total across ALL positions/tiers — the whole pool is one pie
  const globalWeightedTotal = positions.flatMap(pos =>
    pos.tiers.map(tier => (parseFloat(tier.points) || 0) * getHypoHours(tier.id))
  ).reduce((a, b) => a + b, 0);

  const tierHypo = (tier) => {
    const weight      = parseFloat(tier.points) || 0;
    const hours       = getHypoHours(tier.id);
    const weightedHrs = weight * hours;
    if (globalWeightedTotal === 0 || weightedHrs === 0) return { payout: 0, perHour: 0 };
    return {
      payout:  hypoPool * (weightedHrs / globalWeightedTotal),
      perHour: hypoPool * (weight      / globalWeightedTotal),
    };
  };

  const indivHypo = (rate) => {
    if (!rate) return null;
    if (rate.type === 'percent') {
      return (rate.rate / 100) * revenueFor(rate.appliesTo || 'reservation_total', hypo);
    }
    if (rate.type === 'flat') {
      return rate.rate * (parseFloat(hypoReservations) || 1);
    }
    return null;
  };

  // ── Inline edit actions ───────────────────────────────────────────────────

  const openEdit = (emp) => {
    const rate = indivRates[emp.id];
    setEditForm({
      mode:           rate?.mode           || 'individual',
      type:           rate?.type           || 'percent',
      rate:           rate?.rate != null   ? String(rate.rate) : '',
      appliesTo:      rate?.appliesTo      || 'reservation_total',
      positionId:     rate?.positionId     || '',
      tierId:         rate?.tierId         || '',
      pointsOverride: rate?.pointsOverride != null ? String(rate.pointsOverride) : '',
      effectiveStart: todayISO,
      reason:         '',
    });
    setEditingEmpId(emp.id);
    setEditError('');
  };

  const closeEdit = () => { setEditingEmpId(null); setEditError(''); };

  const handleEditSave = async (empId) => {
    if (!editForm.effectiveStart) { setEditError('Effective date is required.'); return; }
    setEditSaving(true);
    setEditError('');
    try {
      await setEmployeeCommissionRate({
        restaurantId,
        employeeId: empId,
        mode:           editForm.mode,
        type:           editForm.mode === 'individual' ? editForm.type       : undefined,
        rate:           editForm.mode === 'individual' ? (parseFloat(editForm.rate) || 0) : undefined,
        appliesTo:      editForm.mode === 'individual' ? editForm.appliesTo  : undefined,
        positionId:     editForm.mode === 'pooled'     ? editForm.positionId : undefined,
        tierId:         editForm.mode === 'pooled'     ? editForm.tierId     : undefined,
        pointsOverride: editForm.mode === 'pooled' && editForm.pointsOverride !== ''
                          ? parseFloat(editForm.pointsOverride) : null,
        effectiveStart: editForm.effectiveStart,
        reason:         editForm.reason.trim() || null,
      });
      closeEdit();
      // onSnapshot will fire because setEmployeeCommissionRate writes commissionMode to the employee doc.
      // If mode stayed 'individual', manually reload rates to pick up the new rate doc.
      if (editForm.mode === 'individual') {
        loadRatesForEmps(indivEmps);
      }
      // If mode changed away from individual, onSnapshot removes them from indivEmps automatically.
    } catch (err) {
      setEditError(err.message || 'Failed to save.');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Derived edit-form values for pooled commission ────────────────────────

  const selectedPosition = positions.find(p => p.id === editForm.positionId);
  const selectedTier     = (selectedPosition?.tiers || []).find(t => t.id === editForm.tierId);
  const allTierPoints    = positions.flatMap(p => p.tiers).reduce((sum, t) => sum + (Number(t.points) || 0), 0);
  const effectivePts     = editForm.pointsOverride !== '' && !isNaN(parseFloat(editForm.pointsOverride))
                             ? parseFloat(editForm.pointsOverride)
                             : (selectedTier ? Number(selectedTier.points) : 0);
  const resultingPct     = allTierPoints > 0 && effectivePts > 0
                             ? ((effectivePts / allTierPoints) * 100).toFixed(1)
                             : null;

  const hasAnyFlatRate = indivEmps.some(e => indivRates[e.id]?.type === 'flat');

  // ── Commission totals for summary panel ──────────────────────────────────────
  const pooledTotal     = poolEnabled ? hypoPool : 0;
  const individualTotal = indivEmps.reduce((sum, emp) => {
    return sum + (indivHypo(indivRates[emp.id]) || 0);
  }, 0);
  const commissionTotal = pooledTotal + individualTotal;

  if (loading) {
    return <p className="text-sm text-neutral-400 text-center py-12">Loading commission settings…</p>;
  }

  return (
    <>
    <div className="space-y-6">

      {/* ── Hypothetical Daily Revenue ────────────────────────────────────── */}
      <div className="bg-white border border-neutral-200 rounded-xl p-4">
        <p className="text-sm font-semibold text-neutral-700 mb-0.5">Hypothetical Daily Revenue</p>
        <p className="text-xs text-neutral-400 mb-3">Used for payout estimates across all commission types.</p>
        <div className="flex flex-wrap items-center gap-4">
          {[{ label: 'Food', key: 'food' }, { label: 'Drinks', key: 'drink' }].map(row => (
            <div key={row.key} className="flex items-center gap-2">
              <span className="text-sm text-neutral-500">{row.label}</span>
              <div className="flex items-center border border-neutral-300 rounded-lg px-2 py-1.5 gap-0.5">
                <span className="text-sm text-neutral-400">$</span>
                <input
                  type="number" min="0" step="1"
                  value={hypo[row.key]}
                  onChange={e => setHypo(h => ({ ...h, [row.key]: e.target.value }))}
                  className="w-20 text-right bg-transparent outline-none text-sm font-medium text-neutral-800"
                />
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-neutral-500">Total</span>
            <span className="text-sm font-semibold text-neutral-800">${hypoTotal.toLocaleString()}</span>
          </div>
          {hasAnyFlatRate && (
            <div className="flex items-center gap-2 border-l border-neutral-200 pl-4">
              <span className="text-sm text-neutral-500">Reservations/day</span>
              <input
                type="number" min="1" step="1"
                value={hypoReservations}
                onChange={e => setHypoReservations(e.target.value)}
                className="w-16 px-2 py-1.5 text-sm border border-neutral-300 rounded-lg text-center"
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Commission Pool toggle + Totals summary ──────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-4 items-start">

        {/* Pool settings card */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5 flex-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-neutral-800">Commission Pool</p>
              <p className="text-sm text-neutral-400 mt-0.5">
                Funds the pool from every reservation. Distributed to pooled employees at period lock.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setPoolEnabled(v => !v); markDirty(); }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${poolEnabled ? 'bg-neutral-900' : 'bg-neutral-200'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${poolEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {poolEnabled && (
            <div className="mt-4 pt-4 border-t border-neutral-100 space-y-4">
              <div className="grid grid-cols-2 gap-3 max-w-sm">
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Inflow Rate</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number" min="0" max="100" step="0.1"
                      value={poolInflowRate}
                      onChange={e => { setPoolInflowRate(e.target.value); markDirty(); }}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    />
                    <span className="text-sm text-neutral-400 shrink-0">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Applied To</label>
                  <select
                    value={poolAppliesTo}
                    onChange={e => { setPoolAppliesTo(e.target.value); markDirty(); }}
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none"
                  >
                    {APPLIES_TO_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-neutral-400">
                Pool = {poolInflowRate || 0}% × ${hypoBase.toLocaleString()} ({APPLIES_TO_OPTIONS.find(o => o.value === poolAppliesTo)?.label}) = <span className="font-semibold text-neutral-600">${hypoPool.toFixed(2)}</span> — split among all pooled employees by weighted hours.
              </p>

              <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3">
                <p className="text-sm font-semibold text-neutral-700 mb-0.5">Weighted Hours Distribution</p>
                <p className="text-sm text-neutral-500 leading-relaxed">
                  One pool is funded at the inflow rate and distributed across <em>all</em> pooled positions
                  by <span className="font-medium">weight × hours worked</span>. Positions and tiers only
                  control each employee's multiplier — they don't create separate pools. A 2× employee
                  earns twice as much per hour as a 1× employee regardless of which position they're in.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Totals summary card */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5 w-full lg:w-52 shrink-0">
          <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-4">
            Daily Totals
          </p>
          <div className="space-y-3">
            {poolEnabled && (
              <div>
                <p className="text-xs text-neutral-400 mb-0.5">Pooled Commission</p>
                <p className="text-xl font-bold text-neutral-800">${pooledTotal.toFixed(2)}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-neutral-400 mb-0.5">Individual Commission</p>
              <p className="text-xl font-bold text-neutral-800">${individualTotal.toFixed(2)}</p>
            </div>
            {(poolEnabled || individualTotal > 0) && (
              <div className="border-t border-neutral-100 pt-3">
                <p className="text-xs font-medium text-neutral-400 mb-0.5">Total Commission</p>
                <p className="text-2xl font-bold text-neutral-900">${commissionTotal.toFixed(2)}</p>
                <p className="text-xs text-neutral-400 mt-1">est. per day</p>
              </div>
            )}
            {!poolEnabled && individualTotal === 0 && (
              <p className="text-xs text-neutral-300 italic">
                Enable the pool or add individual commission rates to see totals.
              </p>
            )}
          </div>
        </div>

      </div>

      {/* ── Positions + Tiers builder ─────────────────────────────────────── */}
      {poolEnabled && (
        <div className="flex flex-col-reverse lg:flex-row gap-5 items-start">

          {/* Left: builder */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wide">
                  Commission Positions & Tiers
                </p>
                <p className="text-xs text-neutral-400 mt-0.5">Weight = multiplier per hour (e.g. 1×, 1.5×, 2×)</p>
              </div>
              <button
                type="button" onClick={addPosition}
                className="text-sm font-semibold px-3 py-1.5 border border-neutral-300 rounded-lg text-neutral-700 hover:border-neutral-500 transition-colors"
              >
                + Add Position
              </button>
            </div>

            {positions.length === 0 ? (
              <div className="border-2 border-dashed border-neutral-200 rounded-xl p-8 text-center">
                <p className="text-sm text-neutral-400">No positions defined yet.</p>
                <p className="text-sm text-neutral-300 mt-1">
                  Add positions like Server or Bartender, then assign tiers with weight multipliers.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {positions.map(pos => (
                  <div key={pos.id} className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-100 bg-neutral-50">
                      <input
                        type="text"
                        value={pos.name}
                        onChange={e => updatePosition(pos.id, 'name', e.target.value)}
                        placeholder="Position name (e.g. Server)"
                        className="flex-1 text-sm font-semibold bg-transparent border-none outline-none text-neutral-800 placeholder-neutral-300"
                      />
                      <button
                        type="button" onClick={() => addTier(pos.id)}
                        className="text-sm text-neutral-500 hover:text-neutral-800 px-2 py-1 border border-neutral-200 rounded-md transition-colors shrink-0"
                      >
                        + Tier
                      </button>
                      <button
                        type="button" onClick={() => removePosition(pos.id)}
                        className="text-sm text-red-400 hover:text-red-600 px-2 py-1 rounded-md transition-colors shrink-0"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="divide-y divide-neutral-50">
                      {pos.tiers.map(tier => (
                        <div key={tier.id} className="flex items-center gap-3 px-4 py-2.5">
                          <input
                            type="text"
                            value={tier.name}
                            onChange={e => updateTier(pos.id, tier.id, 'name', e.target.value)}
                            placeholder="Tier name"
                            className="flex-1 text-sm text-neutral-700 border-none outline-none bg-transparent"
                          />
                          <div className="flex items-center gap-1.5 shrink-0">
                            <input
                              type="number" min="0.1" step="0.5"
                              value={tier.points}
                              onChange={e => updateTier(pos.id, tier.id, 'points', e.target.value)}
                              className="w-16 px-2 py-1 text-sm text-center border border-neutral-300 rounded-lg focus:outline-none"
                            />
                            <span className="text-sm text-neutral-400 w-4">×</span>
                          </div>
                          {pos.tiers.length > 1 && (
                            <button
                              type="button" onClick={() => removeTier(pos.id, tier.id)}
                              className="text-sm text-neutral-300 hover:text-red-400 transition-colors leading-none"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: pool estimates panel (sticky on large, full-width on small) */}
          <div className="w-full lg:w-60 shrink-0 bg-white border border-neutral-200 rounded-xl p-4 space-y-3 lg:sticky lg:top-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">Pool Estimates</p>
              <span className="text-xs text-neutral-400">1 pool · ${hypoPool.toFixed(2)}</span>
            </div>
            <p className="text-xs text-neutral-400">Hours default to 6 — adjust to model your shift.</p>

            {positions.length === 0 ? (
              <p className="text-sm text-neutral-300 italic">Add positions below.</p>
            ) : (
              <div className="space-y-4">
                {positions.map(pos => (
                  <div key={pos.id}>
                    <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1.5">
                      {pos.name || <span className="text-neutral-300 italic normal-case">Unnamed</span>}
                    </p>
                    {pos.tiers.map(tier => {
                      const { payout, perHour } = tierHypo(tier);
                      return (
                        <div key={tier.id} className="mb-2">
                          <div className="flex items-center justify-between gap-1 mb-0.5">
                            <span className="text-sm text-neutral-500 truncate">{tier.name || '—'}</span>
                            <span className="text-xs text-neutral-400 shrink-0">{parseFloat(tier.points) || 0}×</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="flex items-center border border-neutral-200 rounded-md px-1.5 py-0.5 gap-0.5 bg-neutral-50">
                              <input
                                type="number" min="0" step="0.5"
                                value={hypoHours[tier.id] !== undefined ? hypoHours[tier.id] : '6'}
                                onChange={e => setHypoHours(h => ({ ...h, [tier.id]: e.target.value }))}
                                placeholder="6"
                                className="w-10 text-right bg-transparent outline-none text-sm text-neutral-700"
                              />
                              <span className="text-xs text-neutral-400">hrs</span>
                            </div>
                            <div className="flex-1 text-right">
                              {payout > 0 ? (
                                <div>
                                  <p className="text-sm font-semibold text-emerald-600">${payout.toFixed(2)}</p>
                                  <p className="text-xs text-neutral-400">${perHour.toFixed(2)}/hr</p>
                                </div>
                              ) : (
                                <p className="text-xs text-neutral-300">—</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                {globalWeightedTotal > 0 && (
                  <p className="text-xs text-neutral-400 border-t border-neutral-100 pt-2">
                    {globalWeightedTotal.toFixed(1)} total weighted hrs
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Save pool settings ────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave} disabled={saving}
          className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : 'Save Pool Settings'}
        </button>
        {saved && <span className="text-sm text-emerald-600 font-medium">Saved!</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      {/* ── Individual Commissions ────────────────────────────────────────── */}
      <div>
        <div className="mb-3">
          <p className="text-sm font-semibold text-neutral-800">Individual Commissions</p>
          <p className="text-sm text-neutral-400 mt-0.5">
            Employees earning commission on their own reservations at a personal rate.
          </p>
        </div>

        {indivLoading ? (
          <p className="text-sm text-neutral-400 py-4">Loading…</p>
        ) : indivEmps.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl px-5 py-6 text-center">
            <p className="text-sm text-neutral-400">No active employees on individual commission.</p>
            <p className="text-xs text-neutral-300 mt-1">
              Set an employee's commission mode to "Individual" in their profile to appear here.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {indivEmps.map(emp => {
                const rate    = indivRates[emp.id];
                const hypoAmt = indivHypo(rate);
                return (
                  <div key={emp.id} className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col gap-3">
                    {/* Header: name + edit button */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-neutral-800 truncate">
                          {emp.firstName} {emp.lastName}
                        </p>
                        {(emp.position || emp.role) && (
                          <p className="text-xs text-neutral-400 mt-0.5 truncate capitalize">
                            {emp.position || emp.role}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => openEdit(emp)}
                        className="shrink-0 text-sm font-semibold px-3 py-1.5 border border-neutral-300 rounded-lg text-neutral-700 hover:border-neutral-500 transition-colors"
                      >
                        Edit
                      </button>
                    </div>

                    {/* Rate info */}
                    {rate ? (
                      <div className="border-t border-neutral-100 pt-3">
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border capitalize mb-2 ${MODE_COLOR[rate.mode] || MODE_COLOR.none}`}>
                          {rate.mode}
                        </span>
                        <p className="text-sm text-neutral-700">
                          {rate.type === 'percent'
                            ? `${rate.rate}% of ${APPLIES_TO_SHORT[rate.appliesTo || 'reservation_total']}`
                            : `$${Number(rate.rate).toFixed(2)} flat per reservation`}
                        </p>
                        {hypoAmt != null && hypoAmt > 0 && (
                          <p className="text-sm font-semibold text-emerald-600 mt-1">
                            ~${hypoAmt.toFixed(2)}/day
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-neutral-300 italic border-t border-neutral-100 pt-3">
                        No commission rate set
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {hasAnyFlatRate && (
              <p className="text-xs text-neutral-400 mt-2">
                Flat rate estimates based on {hypoReservations} reservation{Number(hypoReservations) !== 1 ? 's' : ''}/day
                — adjust in the revenue panel above.
              </p>
            )}
          </>
        )}
      </div>

    </div>

    {/* ── Edit Commission Modal ─────────────────────────────────────────────── */}
    {editingEmpId && (() => {
      const emp = indivEmps.find(e => e.id === editingEmpId);
      if (!emp) return null;
      const currentRate = indivRates[emp.id];
      return (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center px-4 py-6"
          onClick={e => { if (e.target === e.currentTarget) closeEdit(); }}
        >
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90dvh] flex flex-col shadow-xl" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

            {/* Header */}
            <div className="px-6 py-4 border-b border-neutral-100 shrink-0">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-base font-semibold text-neutral-800">
                    {emp.firstName} {emp.lastName}
                  </p>
                  {(emp.position || emp.role) && (
                    <p className="text-sm text-neutral-400 capitalize mt-0.5">
                      {emp.position || emp.role}
                    </p>
                  )}
                </div>
                <button
                  onClick={closeEdit}
                  className="text-neutral-400 hover:text-neutral-700 text-2xl leading-none shrink-0 mt-0.5"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              {currentRate && (
                <span className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full border capitalize ${MODE_COLOR[currentRate.mode] || MODE_COLOR.none}`}>
                  {currentRate.mode}
                </span>
              )}
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">

              {/* Mode */}
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">Mode</label>
                <div className="flex gap-2">
                  {['none', 'individual', 'pooled'].map(m => (
                    <button
                      key={m} type="button"
                      onClick={() => setEditForm(f => ({ ...f, mode: m }))}
                      className={`flex-1 py-2 text-sm font-medium rounded-lg border capitalize transition-colors ${
                        editForm.mode === m
                          ? 'bg-neutral-900 text-white border-neutral-900'
                          : 'border-neutral-300 text-neutral-600 hover:border-neutral-500'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-neutral-400 mt-1">
                  {editForm.mode === 'none'       && 'No commission earned.'}
                  {editForm.mode === 'individual' && 'Earns commission on their own reservations at their personal rate.'}
                  {editForm.mode === 'pooled'     && 'Share of the restaurant pool based on position, tier weight, and hours worked.'}
                </p>
              </div>

              {/* Individual fields */}
              {editForm.mode === 'individual' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 mb-1">Rate Type</label>
                    <div className="flex gap-2">
                      {['percent', 'flat'].map(t => (
                        <button
                          key={t} type="button"
                          onClick={() => setField('type')(t)}
                          className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${
                            editForm.type === t
                              ? 'bg-neutral-900 text-white border-neutral-900'
                              : 'border-neutral-300 text-neutral-600 hover:border-neutral-500'
                          }`}
                        >
                          {t === 'percent' ? '% of Sale' : 'Flat Rate'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Field
                    label={editForm.type === 'percent' ? 'Commission %' : 'Flat Amount ($)'}
                    type="number"
                    value={editForm.rate}
                    onChange={setField('rate')}
                    placeholder={editForm.type === 'percent' ? '5' : '10.00'}
                  />
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 mb-1">Applies To</label>
                    <select
                      value={editForm.appliesTo}
                      onChange={e => setField('appliesTo')(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none"
                    >
                      {APPLIES_TO_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Pooled fields */}
              {editForm.mode === 'pooled' && (
                <>
                  {positions.length === 0 ? (
                    <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      No commission positions defined. Add positions in the Pool Settings above first.
                    </p>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-neutral-500 mb-1">Position</label>
                        <select
                          value={editForm.positionId}
                          onChange={e => setEditForm(f => ({ ...f, positionId: e.target.value, tierId: '' }))}
                          className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none"
                        >
                          <option value="">— select position —</option>
                          {positions.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                      {selectedPosition && (
                        <div>
                          <label className="block text-sm font-medium text-neutral-500 mb-1">Tier</label>
                          <select
                            value={editForm.tierId}
                            onChange={e => setField('tierId')(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none"
                          >
                            <option value="">— select tier —</option>
                            {selectedPosition.tiers.map(t => (
                              <option key={t.id} value={t.id}>
                                {t.name} ({t.points}× weight)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <Field
                        label={`Weight Override — optional (tier default: ${selectedTier?.points ?? '—'}×)`}
                        type="number"
                        value={editForm.pointsOverride}
                        onChange={setField('pointsOverride')}
                        placeholder={String(selectedTier?.points ?? '')}
                      />
                      {resultingPct && (
                        <p className="text-xs text-neutral-500 -mt-2">
                          At {effectivePts}× out of {allTierPoints.toFixed(1)} total pool points ≈{' '}
                          <span className="font-semibold">{resultingPct}%</span> of pool
                        </p>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Effective date + reason */}
              <Field
                label="Effective Date"
                type="date"
                value={editForm.effectiveStart}
                onChange={setField('effectiveStart')}
              />
              <Field
                label="Reason (optional)"
                value={editForm.reason}
                onChange={setField('reason')}
                placeholder="e.g. promotion, new hire, correction"
              />

              {editError && <p className="text-sm text-red-600">{editError}</p>}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-neutral-100 shrink-0 flex gap-2">
              <button
                onClick={() => handleEditSave(emp.id)}
                disabled={editSaving}
                className="flex-1 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
              >
                {editSaving ? 'Saving…' : 'Save Commission'}
              </button>
              <button
                onClick={closeEdit}
                className="px-4 py-2 text-sm font-semibold border border-neutral-300 rounded-lg text-neutral-600 hover:border-neutral-500 transition-colors"
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      );
    })()}

  </>
  );
};

export default CommissionSettings;
